import moment from "moment/moment";
import { TChartRawData } from "../features/productAnalyzer/productAnalyzer.types";
import Highcharts from "highcharts";

/**
 * Adds data points at the start and end of the chart if the data doesn't cover the full date range.
 * For any gaps at the start/end, it extends the first/last known price value.
 * @param raw - Array of price data points with dates
 * @param minDateTimestamp - Minimum timestamp that should be covered
 * @param maxDateTimestamp - Maximum timestamp that should be covered
 * @returns A new array with additional data points if needed to cover the full date range
 */
export const populateChartRawDataTillDatePoints = (
  raw: TChartRawData[],
  minDateTimestamp?: number,
  maxDateTimestamp?: number,
) => {
  const copy = [...raw];
  const firstItem = raw.at(0);
  const lastItem = raw.at(-1);

  if (
    minDateTimestamp &&
    firstItem &&
    moment(firstItem?.date).isAfter(minDateTimestamp)
  ) {
    copy.unshift({
      price: firstItem.price,
      date: moment(minDateTimestamp).toISOString(),
    });
  }

  if (
    maxDateTimestamp &&
    lastItem &&
    moment(lastItem?.date).isBefore(maxDateTimestamp)
  ) {
    copy.push({
      price: lastItem.price,
      date: moment(maxDateTimestamp).toISOString(),
    });
  }

  return copy;
};

/**
 * Filters an array of price data points to include only those within a specified date range.
 * @param raw - Array of price data points with dates
 * @param startDateTimestamp - Minimum timestamp to include (inclusive)
 * @param endDateTimestamp - Maximum timestamp to include (inclusive)
 * @returns A filtered array containing only data points within the date range
 */
export const filterChartRawDataByDateRange = (
  raw: TChartRawData[],
  startDateTimestamp: number,
  endDateTimestamp: number,
) => {
  return raw.filter(({ date }) => {
    return moment(date).isBetween(
      startDateTimestamp,
      endDateTimestamp,
      null,
      "[]",
    );
  });
};

/**
 * Formats raw chart data into series points for Highcharts
 * @param template - Template for Highcharts series configuration
 * @param raw - Arrays of price data points to be formatted into series data
 * @returns Highcharts series configuration with formatted data points
 */
export const formatChartRawDataToSeriesPoints = (
  template: Highcharts.Options["series"],
  raw: TChartRawData[][],
) => {
  return template?.map((s, i) => ({
    ...s,
    data: raw[i].map(
      ({ date, price }) => [moment(date).valueOf(), price] as const,
    ),
  })) as Highcharts.Options["series"]; // Cast to Highcharts.Options["series"] to avoid type errors
};

/**
 * Creates a tooltip positioner function for Highcharts that positions tooltips at a specified X coordinate
 * while keeping them inside the plot area.
 * @param getXPos - Function that returns the desired X coordinate for the tooltip
 * @returns A tooltip positioner function compatible with Highcharts tooltip configuration
 */
export const createTooltipPositioner = (getXPos: () => number) =>
  function (this: Highcharts.Tooltip, labelWidth: number, labelHeight: number) {
    let x = getXPos();

    // Keep tooltip inside the right edge of the plot
    if (x + labelWidth > this.chart.plotLeft + this.chart.plotWidth) {
      x = x - labelWidth;
    }
    return {
      x,
      y: this.chart.plotTop + this.chart.plotHeight - labelHeight,
    };
  };
